import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ShoppingGuiltVsFinancialAnxietyLanding = ({ goToInput, theme = "mint" }) => {
  // State for quiz responses
  const [shoppingGuiltChecks, setShoppingGuiltChecks] = useState([false, false, false, false, false]);
  const [financialAnxietyChecks, setFinancialAnxietyChecks] = useState([false, false, false, false, false]);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  // Handle checkbox changes for shopping guilt
  const handleShoppingGuiltCheck = (index) => {
    const newChecks = [...shoppingGuiltChecks];
    newChecks[index] = !newChecks[index];
    setShoppingGuiltChecks(newChecks);
  };

  // Handle checkbox changes for financial anxiety
  const handleFinancialAnxietyCheck = (index) => {
    const newChecks = [...financialAnxietyChecks];
    newChecks[index] = !newChecks[index];
    setFinancialAnxietyChecks(newChecks);
  };

  // Calculate quiz results
  const calculateQuizResults = () => {
    const guiltCount = shoppingGuiltChecks.filter(check => check).length;
    const anxietyCount = financialAnxietyChecks.filter(check => check).length;
    
    setQuizCompleted(true);
    setShowQuizResults(true);
    
    return { guiltCount, anxietyCount };
  };

  // Get quiz result message
  const getQuizResultMessage = () => {
    const { guiltCount, anxietyCount } = calculateQuizResults();
    
    if (guiltCount > anxietyCount && guiltCount >= 3) {
      return {
        primary: "You're primarily experiencing Shopping Guilt",
        secondary: "Your responses suggest you feel remorse about specific purchases rather than general financial worry.",
        recommendation: "Focus on mindful purchase decisions and consider implementing a cooling-off period before buying.",
        color: "text-blue-600 dark:text-blue-400",
        emoji: "😟"
      };
    } else if (anxietyCount > guiltCount && anxietyCount >= 3) {
      return {
        primary: "You're primarily experiencing Financial Anxiety",
        secondary: "Your responses indicate broader concerns about your overall financial situation.",
        recommendation: "Consider creating a comprehensive financial plan and building an emergency fund to reduce uncertainty.",
        color: "text-red-600 dark:text-red-400",
        emoji: "😰"
      };
    } else if (guiltCount >= 3 && anxietyCount >= 3) {
      return {
        primary: "You're experiencing both Shopping Guilt and Financial Anxiety",
        secondary: "Your responses show you're dealing with both specific purchase regret and broader money concerns.",
        recommendation: "Work on a comprehensive approach that addresses both immediate spending decisions and long-term financial security.",
        color: "text-purple-600 dark:text-purple-400",
        emoji: "😟😰"
      };
    } else {
      return {
        primary: "Your results are inconclusive",
        secondary: "You may be experiencing mild forms of both or neither shopping guilt nor financial anxiety.",
        recommendation: "Continue monitoring your emotional responses to purchases to better understand your patterns.",
        color: "text-gray-600 dark:text-gray-400",
        emoji: "🤔"
      };
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Shopping Guilt vs. Financial Anxiety: Understanding the Difference
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Identify What's Really Behind Your Shopping Stress</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your purchases, understand whether you're experiencing shopping guilt or financial anxiety, and get personalized strategies for each.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Purchase
            </motion.button>
          </div>
          <div className="text-7xl flex space-x-2 flex-shrink-0">
            <span>😟</span>
            <span>≠</span>
            <span>😰</span>
          </div>
        </div>
      </div>
      
      {/* Key Differences Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Key Differences Between Shopping Guilt & Financial Anxiety</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
          {/* Shopping Guilt Column */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-5">
            <div className="text-center mb-4">
              <span className="text-4xl">😟</span>
              <h3 className="text-xl font-bold mt-2 dark:text-white">Shopping Guilt</h3>
              <div className="text-sm text-gray-500 dark:text-gray-400 italic">Post-purchase emotion</div>
            </div>
            
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Occurs <strong>after</strong> a purchase is made</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Focused on specific items or purchases</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Often tied to questions of necessity and value</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">May involve shame about impulse buying</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Can include ethical concerns about consumption</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 dark:text-blue-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Often resolves once the item is returned or justified</p>
              </li>
            </ul>
          </div>
          
          {/* Financial Anxiety Column */}
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-5">
            <div className="text-center mb-4">
              <span className="text-4xl">😰</span>
              <h3 className="text-xl font-bold mt-2 dark:text-white">Financial Anxiety</h3>
              <div className="text-sm text-gray-500 dark:text-gray-400 italic">Ongoing money worry</div>
            </div>
            
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-red-500 dark:text-red-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Present <strong>before, during, and after</strong> purchases</p>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 dark:text-red-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Concerns overall financial situation and stability</p>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 dark:text-red-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Focused on broader money fears (debt, future security)</p>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 dark:text-red-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">May involve physical symptoms (insomnia, tension)</p>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 dark:text-red-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">Often persists regardless of individual purchases</p>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 dark:text-red-400 font-bold mr-2">•</span>
                <p className="text-gray-700 dark:text-gray-300">May require broader financial planning to address</p>
              </li>
            </ul>
          </div>
        </div>
      </motion.section>
      
      {/* Warning Signs Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Recognizing What You're Experiencing</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛍️</span>
              <h3 className="font-bold mt-2 dark:text-white">Signs of Shopping Guilt</h3>
            </div>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                "Did I really need this?"
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Hide purchases from others
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Return items frequently
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Feel remorse specifically after buying
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Justify purchases to yourself repeatedly
              </li>
            </ul>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">💰</span>
              <h3 className="font-bold mt-2 dark:text-white">Signs of Financial Anxiety</h3>
            </div>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Avoid checking bank accounts
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Worry constantly about money
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Physical stress symptoms when spending
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Insomnia over financial concerns
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span>
                Extreme frugality that impacts quality of life
              </li>
            </ul>
          </div>
        </div>
      </motion.section>
      
      {/* Psychological Differences */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">The Psychology Behind Both Experiences</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold dark:text-white">Shopping Guilt Psychology</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              Shopping guilt is primarily about value conflict and cognitive dissonance. It occurs when your purchase doesn't align with your values (being frugal, environmentally conscious, minimalist) or when you question whether you've made the right choice. It's often temporary and specific to individual purchases.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold dark:text-white">Financial Anxiety Psychology</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              Financial anxiety is a persistent worry state related to broader money concerns. It's often tied to uncertainty about the future, feelings of financial inadequacy, and fear of economic instability. This anxiety can manifest as a constant background stress that affects overall wellbeing and decision-making.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold dark:text-white">Why We Confuse Them</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              These emotions often overlap because both involve money stress. You might feel shopping guilt for a specific purchase while also experiencing background financial anxiety about your overall situation. Understanding which you're primarily dealing with helps you apply the right solutions.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Stats Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">By The Numbers: Two Different Experiences</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">84%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of people experience shopping guilt at least monthly</p>
          </div>
          
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">72%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of Americans report feeling financial anxiety</p>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">68%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">say guilt is highest for non-essential purchases</p>
          </div>
          
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">54%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">report financial anxiety affects their sleep</p>
          </div>
        </div>
      </motion.section>
      
      {/* Different Solutions Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Different Problems, Different Solutions</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Solutions for Shopping Guilt */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-4">
              <span className="text-3xl">🛍️</span>
              <h3 className="font-bold dark:text-white">For Shopping Guilt</h3>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium dark:text-white">Track Purchase Satisfaction</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Log how much you actually use and enjoy each purchase to better understand your true preferences.
                </p>
              </div>
              
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium dark:text-white">Implement Cooling-Off Periods</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Wait 24-72 hours before making non-essential purchases to reduce impulse buying regret.
                </p>
              </div>
              
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium dark:text-white">Create a "Guilt-Free" Fund</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Set aside a specific amount each month that you can spend without questioning or justifying.
                </p>
              </div>
            </div>
          </div>
          
          {/* Solutions for Financial Anxiety */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-4">
              <span className="text-3xl">💰</span>
              <h3 className="font-bold dark:text-white">For Financial Anxiety</h3>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium dark:text-white">Create a Comprehensive Budget</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Develop a complete financial plan that includes saving, investing, and debt reduction strategies.
                </p>
              </div>
              
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium dark:text-white">Build an Emergency Fund</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Gradually save 3-6 months of expenses to create a financial safety net that reduces uncertainty.
                </p>
              </div>
              
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium dark:text-white">Address Underlying Stress</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Practice financial mindfulness and consider therapy if money anxiety significantly impacts your wellbeing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Quiz Section */}
      <motion.section 
        className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 text-center dark:text-white">Quick Assessment: Which Are You Experiencing?</h2>
        
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300 text-center">
            Answer honestly: Which statements resonate more with your current situation?
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-600 dark:text-blue-400 text-center mb-2">Shopping Guilt Signs</h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={shoppingGuiltChecks[0]}
                    onChange={() => handleShoppingGuiltCheck(0)}
                  />
                  <span>I regret specific purchases after making them</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={shoppingGuiltChecks[1]}
                    onChange={() => handleShoppingGuiltCheck(1)}
                  />
                  <span>I often wonder if I really needed what I bought</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={shoppingGuiltChecks[2]}
                    onChange={() => handleShoppingGuiltCheck(2)}
                  />
                  <span>I feel shame about certain shopping behaviors</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={shoppingGuiltChecks[3]}
                    onChange={() => handleShoppingGuiltCheck(3)}
                  />
                  <span>I sometimes hide new purchases from others</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={shoppingGuiltChecks[4]}
                    onChange={() => handleShoppingGuiltCheck(4)}
                  />
                  <span>I'm fine once I return items I regret buying</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold text-red-600 dark:text-red-400 text-center mb-2">Financial Anxiety Signs</h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={financialAnxietyChecks[0]}
                    onChange={() => handleFinancialAnxietyCheck(0)}
                  />
                  <span>I worry constantly about my overall finances</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={financialAnxietyChecks[1]}
                    onChange={() => handleFinancialAnxietyCheck(1)}
                  />
                  <span>I avoid checking my bank accounts or bills</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={financialAnxietyChecks[2]}
                    onChange={() => handleFinancialAnxietyCheck(2)}
                  />
                  <span>Money concerns keep me up at night</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={financialAnxietyChecks[3]}
                    onChange={() => handleFinancialAnxietyCheck(3)}
                  />
                  <span>I feel physical tension when thinking about money</span>
                </li>
                <li className="flex items-start">
                  <input 
                    type="checkbox" 
                    className="mt-1 mr-2" 
                    checked={financialAnxietyChecks[4]}
                    onChange={() => handleFinancialAnxietyCheck(4)}
                  />
                  <span>I worry about financial security even when making small purchases</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-4">
            {!quizCompleted ? (
              <motion.button
                onClick={() => calculateQuizResults()}
                className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                See My Results
              </motion.button>
            ) : (
              <div className="space-y-4">
                {showQuizResults && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-800 p-4 rounded-lg text-center"
                  >
                    <div className="text-2xl mb-2">{getQuizResultMessage().emoji}</div>
                    <h3 className={`font-bold text-lg mb-2 ${getQuizResultMessage().color}`}>
                      {getQuizResultMessage().primary}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                      {getQuizResultMessage().secondary}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm italic">
                      <strong>Recommendation:</strong> {getQuizResultMessage().recommendation}
                    </p>
                  </motion.div>
                )}
                
                <div className="flex flex-col md:flex-row gap-3 justify-center">
                  <motion.button
                    onClick={() => {
                      setShoppingGuiltChecks([false, false, false, false, false]);
                      setFinancialAnxietyChecks([false, false, false, false, false]);
                      setShowQuizResults(false);
                      setQuizCompleted(false);
                    }}
                    className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 font-medium text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Retake Quiz
                  </motion.button>
                  
                  <motion.button
                    onClick={goToInput}
                    className={`px-4 py-2 rounded-xl text-white font-medium text-sm ${buttonStyles[theme]}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Track & Analyze Your Purchases
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.section>
      
      {/* When They Overlap Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">When Shopping Guilt and Financial Anxiety Overlap</h2>
        
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            These emotions often appear together, creating a complex relationship with money and shopping. Here's how they can interact:
          </p>
          
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold dark:text-white">The Anxiety-Guilt Cycle</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Financial anxiety can lead to restrictive spending, followed by "rebellion spending" that then triggers shopping guilt, which reinforces anxiety. Breaking this cycle requires addressing both issues simultaneously.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold dark:text-white">Guilt Intensified by Anxiety</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                What might be minor shopping guilt for someone without financial anxiety can become overwhelming for someone already worried about their financial situation.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold dark:text-white">Integrated Solution Approach</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                When experiencing both, create a structured budget that includes guilt-free spending alongside savings goals. Track both the emotional and financial impact of purchases to identify sustainable patterns.
              </p>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Expert Advice */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Expert Insights</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">On Shopping Guilt</div>
            <p className="text-gray-700 dark:text-gray-300 text-sm italic mb-3">
              "Shopping guilt often stems from unexamined values conflicts. When we clarify our true priorities and align spending with them, guilt naturally diminishes. The goal isn't to never feel guilty, but to make conscious choices that reflect what truly matters to us."
            </p>
            <div className="font-semibold text-sm dark:text-white">Dr. Elizabeth Warren</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Consumer Psychologist</div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">On Financial Anxiety</div>
            <p className="text-gray-700 dark:text-gray-300 text-sm italic mb-3">
              "Financial anxiety is rarely just about money. It's often a manifestation of deeper concerns about security, control, and self-worth. Effective treatment involves both practical financial strategies and addressing these underlying psychological needs."
            </p>
            <div className="font-semibold text-sm dark:text-white">Dr. Brad Klontz</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Financial Psychologist</div>
          </div>
        </div>
      </motion.section>
      
      {/* CTA section */}
      <motion.section 
        className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Understand Your Financial Emotions?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your purchases and identify whether you're dealing with shopping guilt, financial anxiety, or both.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Track Your First Purchase
        </motion.button>
      </motion.section>
      
      {/* FAQ Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Frequently Asked Questions</h2>
        
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Can I experience both shopping guilt and financial anxiety?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes, many people experience both simultaneously. Shopping guilt tends to be purchase-specific, while financial anxiety is a more persistent worry about your overall financial situation. Our tracker helps you identify which is more prominent for you and offers tailored strategies.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is shopping guilt always bad?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Not necessarily. Mild shopping guilt can function as a healthy check on excessive consumption and help you align your spending with your values. However, intense or chronic guilt that affects your wellbeing deserves attention and resolution.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can your tool help with financial anxiety?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Our tool helps you identify patterns in your emotional responses to spending, set realistic budgeting goals, and track your progress in developing a healthier relationship with money. For more severe financial anxiety, we also provide resources for professional financial and psychological support.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Does financial therapy really work for shopping-related issues?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes, financial therapy - which combines financial planning with therapeutic techniques - can be very effective for both shopping guilt and financial anxiety. It addresses both the practical money management aspects and the emotional components that drive problematic spending patterns.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I tell if my shopping guilt is normal or problematic?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Occasional shopping guilt is normal and can even be helpful for mindful spending. It becomes problematic when it's frequent, intense, causes significant distress, leads to hiding purchases, or interferes with your daily life. Our assessment tool can help you determine if your guilt patterns warrant further attention.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What's the difference between financial anxiety and being financially responsible?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial responsibility involves thoughtful planning and informed decisions about money, while financial anxiety is characterized by excessive worry, avoidance behaviors, and emotional distress. Responsible financial behavior feels empowering, while anxiety feels overwhelming and often paralyzing.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Practical Tips Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Quick Tips for Both Issues</h2>
        
        <div className="flex overflow-x-auto py-2 space-x-4 no-scrollbar">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[260px] max-w-[260px] flex-shrink-0">
            <div className="text-xl mb-2 text-center">📝</div>
            <h3 className="font-semibold text-center mb-2 dark:text-white">Track Every Purchase</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Recording purchases increases awareness of both specific items causing guilt and overall spending patterns that trigger anxiety.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[260px] max-w-[260px] flex-shrink-0">
            <div className="text-xl mb-2 text-center">⏰</div>
            <h3 className="font-semibold text-center mb-2 dark:text-white">Implement Waiting Periods</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              A 24-hour rule for non-essentials reduces both impulse buys that cause guilt and spontaneous spending that triggers anxiety.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[260px] max-w-[260px] flex-shrink-0">
            <div className="text-xl mb-2 text-center">🧠</div>
            <h3 className="font-semibold text-center mb-2 dark:text-white">Practice Mindfulness</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Being present during purchases helps identify the emotions driving your buying decisions and reduces post-purchase regret.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[260px] max-w-[260px] flex-shrink-0">
            <div className="text-xl mb-2 text-center">💰</div>
            <h3 className="font-semibold text-center mb-2 dark:text-white">Create a "Fun Money" Fund</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Designate a specific amount for guilt-free discretionary spending while keeping overall finances structured to reduce anxiety.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[260px] max-w-[260px] flex-shrink-0">
            <div className="text-xl mb-2 text-center">🗣️</div>
            <h3 className="font-semibold text-center mb-2 dark:text-white">Talk About Money</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Discussing finances with trusted friends or a professional can reduce both the shame of shopping guilt and isolation of financial anxiety.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Shopping Guilt and Financial Anxiety</h2>
          <p className="mb-3">
            Shopping guilt and financial anxiety are distinct emotional experiences related to money and purchases. While shopping guilt is a post-purchase emotion focused on specific items and questions of value or necessity, financial anxiety is a broader, persistent worry about overall financial stability and security.
          </p>
          <p className="mb-3">
            Our tool helps you identify which emotional pattern dominates your relationship with shopping and provides targeted strategies for both issues. By understanding the differences between these feelings, you can develop more effective approaches to achieving both financial wellness and shopping satisfaction.
          </p>
          <p>
            Whether you're experiencing the specific regret of an impulse purchase or the ongoing stress of financial uncertainty, our free tracker helps you understand your emotional patterns and develop healthier spending habits tailored to your unique situation.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default ShoppingGuiltVsFinancialAnxietyLanding;
